import React, { useEffect, useMemo, useState } from 'react';

import throttle from 'lodash/throttle';
import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'react-use';

import { type NFTManagerFilter } from '@app/store/types/nft-manager';

import { ListNumberInput, RangeOptions } from './styles';

const MAX = 'max';
const MIN = 'min';
const CURRENCY_ETH = 'eth';
const STEP = 0.01;

export type InputFloorPriceRangeOnChange = Pick<
    NFTManagerFilter,
    'floorPriceGe' | 'floorPriceLe' | 'floorPriceCurrency'
>;

export type InputFloorPriceRangeValues = Omit<InputFloorPriceRangeOnChange, 'floorPriceCurrency'>;

type InputFloorPriceRangeProps = {
    onChange: (values: InputFloorPriceRangeOnChange) => void;
    /** Used to set the initial state of the inputs. For example from URLSearchParams */
    initialState?: InputFloorPriceRangeValues;
    isWide?: boolean;
};

/**
 * InputFloorPriceRange is a component that allows the user to search a floor price range.
 * floorPriceGe: The minimum value of the floor price.
 * floorPriceLe: The maximum value of the floor price.
 */
export const InputFloorPriceRange = ({ onChange, initialState, isWide }: InputFloorPriceRangeProps) => {
    const { t } = useTranslation(['yat_nft_manager']);
    const [state, setState] = useState<InputFloorPriceRangeValues | undefined>(initialState);

    useEffect(() => {
        if (!initialState?.floorPriceGe && !!state?.floorPriceGe) {
            setState((currState) => ({ ...currState, floorPriceGe: initialState?.floorPriceGe }));
        }
        if (!initialState?.floorPriceLe && !!state?.floorPriceLe) {
            setState((currState) => ({ ...currState, floorPriceLe: initialState?.floorPriceLe }));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialState]);

    useUpdateEffect(() => {
        if (state) {
            throttledUpdate(state);
        }
    }, [state]);

    const throttledUpdate = useMemo(
        () =>
            throttle(
                (input: InputFloorPriceRangeValues) => onChange({ ...input, floorPriceCurrency: CURRENCY_ETH }),
                300,
            ),
        [onChange],
    );

    const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
        const type = event.currentTarget.name;
        let value: number | undefined = event.currentTarget.valueAsNumber;

        if (isNaN(value) || value < 0) {
            value = undefined;
        }

        if (type === MIN) {
            setState((prevState) => ({ ...prevState, floorPriceGe: value }));
            return;
        }
        if (type === MAX) {
            setState((prevState) => ({ ...prevState, floorPriceLe: value }));
            return;
        }
    };

    /**
     * Basic validation to disallow certain allowed values on a input type='number'.
     */
    const handleOnKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) =>
        ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();

    return (
        <RangeOptions isWide={isWide}>
            <ListNumberInput isWide={isWide}>
                <input
                    type="number"
                    placeholder={t('yat_nft_manager_filter_price_min')}
                    min={0}
                    max={state?.floorPriceLe}
                    step={STEP}
                    id={MIN}
                    name={MIN}
                    onChange={handleInput}
                    onKeyDown={handleOnKeyPress}
                    value={state?.floorPriceGe != null ? state.floorPriceGe : ''}
                />
            </ListNumberInput>
            <ListNumberInput isWide={isWide}>
                <input
                    type="number"
                    placeholder={t('yat_nft_manager_filter_price_max')}
                    min={state?.floorPriceGe || 0}
                    step={STEP}
                    id={MAX}
                    name={MAX}
                    onChange={handleInput}
                    onKeyDown={handleOnKeyPress}
                    value={state?.floorPriceLe != null ? state?.floorPriceLe : ''}
                />
            </ListNumberInput>
        </RangeOptions>
    );
};
